import { useState, useRef } from "react";
import axios from "axios";
import { Lock, Signal, Eye, EyeOff, AlertCircle } from "lucide-react";
import { Toaster } from "sonner";
import { saveSession } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setError("Wprowadź hasło.");
      inputRef.current?.focus();
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      saveSession(data);
      onSuccess();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setError("Nieprawidłowe hasło. Spróbuj ponownie.");
      } else if (status === 503) {
        setError("Logowanie chwilowo niedostępne. Skontaktuj się z administratorem.");
      } else {
        setError("Wystąpił błąd. Spróbuj ponownie za chwilę.");
      }
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh bg-gradient-to-br from-slate-50 to-emerald-50/40 flex items-center justify-center px-4">
      <Toaster position="top-center" richColors />
      <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-emerald-900/5 p-8 w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <Signal className="w-5 h-5 text-emerald-700" aria-hidden="true" />
          <span className="font-display font-bold text-slate-900">
            Telecom <span className="text-emerald-600">BTS</span>
          </span>
        </div>

        <h1 className="text-2xl font-black text-slate-900 mb-1 tracking-tight">
          Panel Administratora
        </h1>
        <p className="text-slate-600 text-sm mb-6">
          Podaj hasło, aby uzyskać dostęp do zgłoszeń.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <label htmlFor="admin-password" className="sr-only">
              Hasło administratora
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" aria-hidden="true" />
              <input
                ref={inputRef}
                id="admin-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
                placeholder="Hasło administratora"
                autoComplete="current-password"
                aria-invalid={!!error}
                aria-describedby={error ? "admin-password-err" : undefined}
                className={`w-full pl-10 pr-11 h-12 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-shadow ${
                  error
                    ? "border-red-400 focus:ring-red-500 focus:border-transparent"
                    : "border-slate-200 focus:ring-emerald-500 focus:border-transparent"
                }`}
                data-testid="admin-password-input"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1 -m-1 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                aria-label={showPassword ? "Ukryj hasło" : "Pokaż hasło"}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {error && (
              <p id="admin-password-err" role="alert" className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !password.trim()}
            className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-full transition-all duration-200 hover:-translate-y-0.5 disabled:translate-y-0 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 shadow-lg shadow-emerald-900/20"
            data-testid="admin-login-button"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                <span>Sprawdzanie...</span>
              </>
            ) : (
              <span>Zaloguj się</span>
            )}
          </button>
        </form>

        <p className="text-xs text-slate-500 text-center mt-6">
          Strefa wewnętrzna Telecom BTS
        </p>
      </div>
    </div>
  );
}
